import { createApi, createSlice } from 'utils/@reduxjs/toolkit';
import { baseQuery, endpoints, formatErrors } from 'utils/api/endpoints';
import { useInjectReducer } from 'utils/redux-injectors';
import { AppointmentListState, Appointment, AppointmentListResponse, AppointmentSummaryResponse } from './types';

// RTK Query API for appointments
export const api = createApi({
  reducerPath: 'appointmentListApi',
  baseQuery,
  tagTypes: ['Appointment', 'Notes', 'Notification', 'AppointmentSummary'],
  endpoints: builder => ({
    getAppointmentsByProvider: builder.query<AppointmentListResponse, { 
      providerId: string; 
      patientType?: string[]; 
      status?: string[]; 
      name?: string;
      startDate?: string;
      endDate?: string;
      appointmentDate?: boolean;
      priority?: string[];
      referral?: string[];
      page?: number;
      limit?: number;
      source?: string[];
      timezone?: string;
    }>({
      query: ({ providerId, patientType, status, name, startDate, endDate, appointmentDate, priority, page = 1, limit = 20, source, timezone }) => {
        return {
          url: endpoints.appointmentListByProvider.url.replace(':providerId', providerId) + 
            (patientType && patientType.length > 0 ? `&patientType=${JSON.stringify(patientType)}` : '') +
            (status && status.length > 0 ? `&status=${JSON.stringify(status)}` : '') +
            (name ? `&name=${name}` : '') +
            (startDate ? `&startDate=${startDate}` : '') +
            (endDate ? `&endDate=${endDate}` : '') +
            (appointmentDate !== undefined ? `&AppointmentDate=${appointmentDate}` : '') +
            (priority && priority.length > 0 ? `&priority=${JSON.stringify(priority)}` : '') +
            (source && source.length > 0 ? `&source=${JSON.stringify(source)}` : '') +
            (timezone ? `&timezone=${timezone}` : '') +
            `&page=${page}` +
            `&limit=${limit}`,
          method: endpoints.appointmentListByProvider.method,
        };
      },
      transformResponse: (response: any) => response || null,
      transformErrorResponse(baseQueryReturnValue, meta, arg) {
        return formatErrors(baseQueryReturnValue.data);
      },
      providesTags: ['Appointment'],
    }),
    // Appointment Request Form data
    getAppointmentById: builder.query<Appointment, { appointmentId: string }>({
      query: ({ appointmentId }) => ({
        url: endpoints.appointmentGetById.url.replace(':appointmentId', appointmentId),
        method: endpoints.appointmentGetById.method,
      }),
      transformResponse: (response: any) => response || null,
    }),
    // Get notes for an appointment
    getNotesByAppointment: builder.query<any[], { appointmentId: string }>({
      query: ({ appointmentId }) => ({
        url: endpoints.noteListByAppointment.url.replace(':appointmentId', appointmentId.toString()),
        method: endpoints.noteListByAppointment.method,
      }),
      transformResponse: (response: any) => response || [],
      providesTags: ['Notes'],
    }),
    // Admin Notes data mutation
    addNoteToAppointment: builder.mutation<any, { appointmentId: any; text: string }>({
      query: ({ appointmentId, text }) => ({
        url: endpoints.noteAddToAppointment.url.replace(':appointmentId', appointmentId),
        method: endpoints.noteAddToAppointment.method,
        body: { text },
      }),
      invalidatesTags: ['Notes'],
    }),
       
    changeAppointmentStatus: builder.mutation<any, { appointmentId: number; body: any }>({
      query: ({ appointmentId, body }) => ({
        url: endpoints.appointmentStatusChange.url.replace(':appointmentId', appointmentId.toString()),
        method: endpoints.appointmentStatusChange.method,
        body: body,
      }),
      invalidatesTags: ['Appointment', 'AppointmentSummary'],
      // Force refetch of related queries
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          // Invalidate and refetch both appointment list and summary queries
          dispatch(api.util.invalidateTags(['Appointment', 'AppointmentSummary']));
          // Force refetch of all appointment queries
          dispatch(api.util.invalidateTags(['Appointment']));
        } catch (error) {
          console.error('Status update failed:', error);
        }
      },
    }),
    // Add appointment
    addAppointment: builder.mutation<any,any>({
      query: (body:any) => ({
        url: endpoints.appointmentCreate.url,
        method: endpoints.appointmentCreate.method,
        body: body,
      }),
      transformResponse: (response: any) => response || null, 
      transformErrorResponse(baseQueryReturnValue, meta, arg) {
        return formatErrors(baseQueryReturnValue.data);
      },
      invalidatesTags: ['Appointment', 'AppointmentSummary'],
    }),
    // Update appointment
    updateAppointment: builder.mutation<any, { appointmentId: string; body: any }>({
      query: ({ appointmentId, body }) => ({
        url: endpoints.appointmentUpdate.url.replace(':appointmentId', appointmentId),
        method: endpoints.appointmentUpdate.method,
        body: body,
      }),
      transformResponse: (response: any) => response || null,
      transformErrorResponse(baseQueryReturnValue, meta, arg) {
        return formatErrors(baseQueryReturnValue.data);
      },
      invalidatesTags: ['Appointment', 'AppointmentSummary'],
    }),
    // Get appointment summary
    getAppointmentSummary: builder.query<AppointmentSummaryResponse, void>({
      query: () => ({
        url: endpoints.appointmentSummary.url,
        method: endpoints.appointmentSummary.method,
      }),
      transformResponse: (response: any) => response || null,
      transformErrorResponse(baseQueryReturnValue, meta, arg) {
        return formatErrors(baseQueryReturnValue.data);
      },
      providesTags: ['Appointment', 'AppointmentSummary'],
    }),

  }),
});

// Slice for local state (loading/error/data if needed for non-API state)
const initialState: AppointmentListState = {
  loading: false,
  error: null,
  data: [],
};

const slice = createSlice({
  name: 'appointmentList',
  initialState,
  reducers: {},
});

export const { actions: appointmentListActions, reducer: appointmentListReducer } = slice;

export const useAppointmentListSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectReducer({ key: api.reducerPath, reducer: api.reducer });
	
  // RTK Query injects itself
  return {
    actions: slice.actions,
    ...api,
  };
};

